import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AuthenticationResult } from 'src/app/models/AuthenticationResult';
import { Permissions } from 'src/app/models/Enums/Permissions';
import { Roles } from 'src/app/models/Enums/Roles';

import { AuthenticationService } from '../authentication.service';
import { RolePermissionsMap } from './permission-mapper';

@Injectable({ providedIn: 'root' })
export class PermissionService implements OnDestroy {
  private currentRole: string;
  private authenticationService = inject(AuthenticationService);
  private destroy$ = new Subject<void>();

  constructor() {
    this.authenticationService.currentAuth$
      .pipe(takeUntil(this.destroy$))
      .subscribe((authResult: AuthenticationResult) => {
        const allowedRoles = [
          Roles.ISR,
          Roles.RSA,
          Roles.SalesSupport,
          Roles.SalesAdmin,
          Roles.DivisionManager
        ];
        const roles = (authResult?.user?.roles || []).filter((role) =>
          allowedRoles.includes(role as Roles),
        );
        this.currentRole = roles?.[0];
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public isRsa(): boolean {
    return this.currentRole?.includes(Roles.RSA);
  }

  public isIsr(): boolean {
    return this.currentRole?.includes(Roles.ISR);
  }

  public isSalesAdmin(): boolean {
    return this.currentRole?.includes(Roles.SalesAdmin);
  }

  public isDivisionManager(): boolean {
    return this.currentRole?.includes(Roles.DivisionManager);
  }

  public isSalesSupport(): boolean {
    return this.currentRole?.includes(Roles.SalesSupport);
  }

  public hasPermission(permission: Permissions): boolean {
    const permissions = RolePermissionsMap[this.currentRole as Roles] || [];
    return permissions.includes(permission);
  }

  public hasAnyPermission(permissions: Permissions[]): boolean {
    return permissions.some((p) => this.hasPermission(p));
  }
}
